import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  InputBase,
  MenuItem,
  Select,
  Skeleton,
} from "@mui/material";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { defaultCountries, parseCountry } from "react-international-phone";
import {
  handleErrorMessage,
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "helpers/methods";
import notifiers from "global/constants/NotificationConstants";
import { useAppSelector } from "hooks/use-app-select";
import {
  addPaidCustomer,
  addPaymentMethod,
  setDefaultCard,
} from "screens/Settings/MyPlan/PlanService";
import { switchPlan } from "Services/CommonServices";
import {
  getSeatBlockReason,
  getSeatReductionFromError,
  isKeepSelectionRejected,
  SeatBlockReason,
} from "helpers/perSeatHelpers";
import { SeatReductionRequired } from "types/perSeat";
import checkoutTokens from "screens/Settings/MyPlan/Checkout.tokens";
import {
  annualNoteSx,
  annualNoteTextSx,
  backLinkSx,
  brandPlateSx,
  cardBoxSx,
  cardHeadSx,
  cardLineSx,
  cardLogoSx,
  cardNumberRowSx,
  cardSplitCellSx,
  cardSplitRowSx,
  cardSubSx,
  cardSx,
  cardTitleSx,
  changeLinkSx,
  checkoutGridSx,
  checkoutViewSx,
  chevronSx,
  countryColumnSx,
  countryMenuItemSx,
  countrySelectSx,
  fieldGroupSx,
  fieldLabelSx,
  fieldRowSx,
  networkChipRowSx,
  networkChipSx,
  newCardOptionSx,
  optionLabelSx,
  optionRowSx,
  optionShellSx,
  optionTargetSx,
  paymentHeadSx,
  payButtonSx,
  pickerRowSx,
  pickerSx,
  poweredBySx,
  primaryPillSx,
  radioDotSx,
  radioSx,
  renewNoteSx,
  secureNoteSx,
  stripeFieldSx,
  stripeWordmarkSx,
  summaryLabelSx,
  summaryRowSx,
  summaryRowsSx,
  summaryUnitSx,
  summaryValueSx,
  textFieldSx,
  totalLabelSx,
  totalRowSx,
  totalValueSx,
  zipColumnSx,
} from "screens/Settings/MyPlan/Checkout.styles";
import {
  CheckoutCard,
  CheckoutSummary,
  formatMaskedCard,
  toCheckoutCards,
} from "screens/Settings/MyPlan/components/CheckoutHelper";
import { PlanPromoNote } from "screens/Settings/MyPlan/components/PlanPromo";
import { PromoCampaign } from "helpers/promotionHelpers";
import {
  AnnualCheckIcon,
  CheckoutBackArrowIcon,
  ChevronDownIcon,
  PickerCheckIcon,
  SecureLockIcon,
} from "./CheckoutIcons";

const t = checkoutTokens;

/** Networks advertised inside the card-number field, per the design. */
const ACCEPTED_NETWORKS = [
  { label: "VISA", color: t.networkChip.visa },
  { label: "MC", color: t.networkChip.mastercard },
  { label: "DISC", color: t.networkChip.discover },
];

const DEFAULT_COUNTRY = "us";

/**
 * Settings → My Plan → Checkout. Order summary on the left, Stripe payment on
 * the right: pick a saved card or enter a new one through Stripe Elements.
 */
const PlanCheckout = (props: {
  summary: CheckoutSummary;
  selectedPlan: any;
  isPlanAnnual: boolean;
  /**
   * Seasonal campaign the backend applies to this order — set only when the
   * selected plan's sale is chargeable (`getPlanSale`). Shows the campaign
   * note, and asks the backend to apply the code (`discount=true`).
   */
  promoCampaign?: PromoCampaign | null;
  /** The account-credit call is still in flight; the totals aren't final yet. */
  isSummaryLoading?: boolean;
  /** Saved cards from the payment-methods API. */
  cardData: any[];
  getUserData?: Function;
  /**
   * Seats being bought. Sent with the purchase *and* with a plan switch, so the
   * subscription bills exactly the number quoted here — the backend never
   * infers it from the user count.
   */
  seats: number;
  /**
   * Whether this plan is sold by the seat. A flat-billed plan bills at quantity
   * 1 whatever the account's size, and its endpoints take no seat count — so
   * the purchase is sent without one rather than with a number the backend
   * would ignore.
   */
  isPerSeat?: boolean;
  /**
   * Users to keep when the switch is a downgrade that needs a seat reduction.
   * Without it the API answers `409` and the picker has to be shown.
   */
  keepUserIds?: number[];
  /** The switch came back `409`: render the keep-picker from this body. */
  onSeatReductionRequired?: (reduction: SeatReductionRequired) => void;
  /**
   * The switch came back `406` about the keep-list itself — someone on it is no
   * longer billable. The roster has to be fetched again before the customer can
   * pick, so the owner of that call handles it.
   */
  onKeepSelectionStale?: (message?: string) => void;
  /** The switch came back `406` (plan full) or `402` (card declined). */
  onSeatBlocked?: (reason: SeatBlockReason, message?: string) => void;
  onBack: () => void;
  onDone: () => void;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { email, userName, account } = useAppSelector((state) => state.auth);

  const cards = useMemo(
    () => toCheckoutCards(props.cardData).filter((card) => !card.isExpired),
    [props.cardData],
  );

  const [useNewCard, setUseNewCard] = useState<boolean>(cards.length === 0);
  const [selectedCardId, setSelectedCardId] = useState<string>(
    () => (cards.find((card) => card.isPrimary) ?? cards[0])?.id ?? "",
  );
  const [isPickerOpen, setPickerOpen] = useState<boolean>(false);
  const [cardName, setCardName] = useState<string>(userName ?? "");
  const [country, setCountry] = useState<string>(DEFAULT_COUNTRY);
  const [zip, setZip] = useState<string>("");
  const [completeFields, setCompleteFields] = useState({
    number: false,
    expiry: false,
    cvc: false,
  });
  const [isPaying, setIsPaying] = useState<boolean>(false);
  const cardFieldsRef = useRef<HTMLDivElement | null>(null);

  const selectedCard = cards.find((card) => card.id === selectedCardId);

  useEffect(() => {
    const container = cardFieldsRef.current;
    if (!useNewCard || !container) return undefined;

    const hideStripeAutofillShims = () => {
      container
        .querySelectorAll<HTMLInputElement>("input.__PrivateStripeElement-input")
        .forEach((input) => {
          if (input.getAttribute("tabindex") === "-1") return;
          input.setAttribute("tabindex", "-1");
        });
    };

    hideStripeAutofillShims();
    const observer = new MutationObserver(hideStripeAutofillShims);
    observer.observe(container, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [useNewCard]);

  // Cards can land after the first render; keep a usable card selected (and
  // fall back to the new-card form when none is left).
  useEffect(() => {
    if (cards.length === 0) {
      setUseNewCard(true);
      return;
    }
    setSelectedCardId((current) =>
      cards.some((card) => card.id === current)
        ? current
        : ((cards.find((card) => card.isPrimary) ?? cards[0]).id ?? ""),
    );
  }, [cards]);

  const countries = useMemo(
    () =>
      defaultCountries
        .map((entry) => parseCountry(entry))
        .map((entry) => ({ name: entry.name, iso2: entry.iso2 }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [],
  );

  /** Stripe renders inside an iframe, so its type has to be declared here. */
  const elementOptions = useMemo(
    () => ({
      style: {
        base: {
          fontFamily: "'Montserrat', sans-serif",
          fontSize: t.font.fieldText,
          fontWeight: String(t.weight.field),
          color: t.color.ink,
          "::placeholder": { color: t.color.placeholder },
        },
        invalid: { color: t.color.accent, iconColor: t.color.accent },
      },
    }),
    [],
  );

  const isNewCardReady =
    completeFields.number &&
    completeFields.expiry &&
    completeFields.cvc &&
    isTruthy(cardName.trim());

  const canPay =
    !isPaying &&
    // Don't take a payment while the order's total is still resolving.
    !props.isSummaryLoading &&
    Boolean(stripe) &&
    (useNewCard ? isNewCardReady : Boolean(selectedCard));

  const selectSavedCard = (id: string) => {
    setSelectedCardId(id);
    setUseNewCard(false);
    setPickerOpen(false);
  };

  const createStripePaymentMethod = async () => {
    const numberElement = elements?.getElement(CardNumberElement);
    if (!stripe || !numberElement) return null;
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: numberElement,
      billing_details: {
        name: cardName,
        email,
        address: { country: country.toUpperCase(), postal_code: zip },
      },
    });
    if (error) {
      openErrorNotification(error.message ?? notifiers.GENERIC_ERROR);
      return null;
    }
    return paymentMethod;
  };

  /**
   * The quantity to buy. Sent with **every** purchase path on a per-seat plan —
   * checkout and plan switch alike — because the backend never infers it: with
   * no `seats` it bills the account's active-user count instead of the number
   * quoted on this screen, and rewrites the seat limit to match.
   */
  const billedSeats =
    props.isPerSeat === false ? undefined : Math.max(1, Math.floor(props.seats));

  const handlePay = async () => {
    try {
      setIsPaying(true);
      // Annual plans always ask for their promotion code; a seasonal sale
      // needs it on monthly too, or the backend charges the full price.
      const discountFlag = props.isPlanAnnual || Boolean(props.promoCampaign);

      if (useNewCard) {
        const paymentMethod = await createStripePaymentMethod();
        if (!paymentMethod) {
          setIsPaying(false);
          return;
        }
        if (props.cardData.length > 0) {
          // Card on file already exists: register the new one as primary and
          // let the plan change bill against it.
          await addPaymentMethod(paymentMethod.id, true);
          await switchPlan(
            props.selectedPlan?.id,
            discountFlag,
            props.keepUserIds,
            billedSeats,
          );
        } else {
          await addPaidCustomer(
            {
              app: "inkfree",
              account,
              name: userName,
              contactNo: "",
              planId: props.selectedPlan?.id,
              paymentMethodId: paymentMethod.id,
              email,
              // Buy-upfront: the subscription starts at the seats bought here,
              // and `keepUserIds` says who occupies them when the account
              // already has more people than seats.
              ...(billedSeats ? { seats: billedSeats } : {}),
              ...(props.keepUserIds ? { keepUserIds: props.keepUserIds } : {}),
            },
            discountFlag,
          );
        }
      } else {
        if (selectedCard && !selectedCard.isPrimary) {
          await setDefaultCard(selectedCard.id);
        }
        // Same seat count as every other path through this screen: a switch
        // without it is billed on the active-user count, not on what was
        // quoted here.
        await switchPlan(
          props.selectedPlan?.id,
          discountFlag,
          props.keepUserIds,
          billedSeats,
        );
      }

      // Keep the button's busy spinner (and `canPay`'s block on re-submitting)
      // through the full refetch — closing out right after the purchase call
      // resolves would drop the customer back onto a plan card still showing
      // the pre-purchase plan for however long the refetch takes.
      if (props.getUserData) {
        await props.getUserData();
      }
      openSuccessNotification(
        "Success! Your plan has been updated. Changes will be applied to your account within 24 hours.",
      );
      setIsPaying(false);
      props.onDone();
    } catch (error: any) {
      setIsPaying(false);
      // A downgrade that still needs a seat reduction comes back as 409 with
      // the users to choose from — even when the check was run beforehand.
      const reduction = getSeatReductionFromError(error);
      if (reduction && props.onSeatReductionRequired) {
        props.onSeatReductionRequired(reduction);
        return;
      }
      // A 406 answering the keep-list is about that list, not about the plan
      // being full — an upgrade prompt would send them the wrong way.
      if (
        isKeepSelectionRejected(error, props.keepUserIds) &&
        props.onKeepSelectionStale
      ) {
        props.onKeepSelectionStale(error?.message);
        return;
      }
      const reason = getSeatBlockReason(error);
      if (reason && props.onSeatBlocked) {
        props.onSeatBlocked(reason, error?.message);
        return;
      }
      handleErrorMessage(error);
    }
  };

  /** Network logo, falling back to a text plate for unmapped brands. */
  const getBrandMark = (card: CheckoutCard) =>
    card.logo ? (
      <Box
        component="img"
        src={card.logo}
        alt={`${card.network} credit card logo`}
        sx={cardLogoSx}
      />
    ) : (
      <Box component="span" sx={brandPlateSx(card.brandColor)}>
        {card.brand}
      </Box>
    );

  const getSummaryCard = () => (
    <Box sx={cardSx(t.space.summaryGap)}>
      <Box sx={cardHeadSx}>
        <Box component="h2" sx={cardTitleSx}>
          {props.summary.title}
        </Box>
        <Box component="span" sx={cardSubSx}>
          {props.summary.subtitle}
        </Box>
      </Box>

      {props.promoCampaign && (
        <PlanPromoNote
          campaign={props.promoCampaign}
          isAnnual={props.isPlanAnnual}
        />
      )}

      <Box sx={summaryRowsSx}>
        <Box sx={summaryRowSx}>
          <Box component="span" sx={summaryLabelSx(true)}>
            {props.summary.planLine}
          </Box>
          <Box component="span" sx={summaryValueSx}>
            {props.summary.monthly}{" "}
            <Box component="span" sx={summaryUnitSx}>
              / month
            </Box>
          </Box>
        </Box>

        {props.summary.showAnnualNote && (
          <Box sx={annualNoteSx}>
            <AnnualCheckIcon sx={{ color: t.color.success }} />
            <Box component="span" sx={annualNoteTextSx}>
              {props.summary.annualNote}
            </Box>
          </Box>
        )}

        {/* Laid out as the Stripe invoice lays it out: line items, their
            total, the account balance spent against it, then what the card is
            actually charged. */}
        {props.summary.lines.map((line) => (
          <Box sx={summaryRowSx} key={line.label}>
            <Box component="span" sx={summaryLabelSx(false)}>
              {line.label}
            </Box>
            <Box component="span" sx={summaryValueSx}>
              {line.value}
            </Box>
          </Box>
        ))}

        <Box sx={summaryRowSx}>
          <Box component="span" sx={summaryLabelSx(true)}>
            Total
          </Box>
          <Box component="span" sx={summaryValueSx}>
            {props.summary.total}
          </Box>
        </Box>

        {/* Saved credit decides both of these, so they wait for it rather than
            showing a number that is about to change. */}
        {props.isSummaryLoading ? (
          <Box sx={summaryRowSx}>
            <Box component="span" sx={summaryLabelSx(false)}>
              Applied balance
            </Box>
            <Skeleton width={70} height={20} />
          </Box>
        ) : (
          props.summary.showAppliedBalance && (
            <Box sx={summaryRowSx}>
              <Box component="span" sx={summaryLabelSx(false)}>
                Applied balance
              </Box>
              <Box component="span" sx={summaryValueSx}>
                {props.summary.appliedBalance}
              </Box>
            </Box>
          )
        )}

        <Box sx={totalRowSx}>
          <Box component="span" sx={totalLabelSx}>
            Total due
          </Box>
          {props.isSummaryLoading ? (
            <Skeleton width={90} height={32} />
          ) : (
            <Box component="span" sx={totalValueSx}>
              {props.summary.due}
            </Box>
          )}
        </Box>
      </Box>

      <Box component="span" sx={renewNoteSx}>
        {props.summary.creditNote
          ? `${props.summary.creditNote} ${props.summary.renewNote}`
          : props.summary.renewNote}
      </Box>
    </Box>
  );

  const getSavedCardOption = () => {
    if (cards.length === 0 || !selectedCard) return null;
    return (
      <Box sx={optionShellSx(!useNewCard)}>
        <Box sx={optionRowSx} onClick={() => selectSavedCard(selectedCard.id)}>
          <ButtonBase
            id="checkout-saved-card-option"
            disableRipple
            aria-pressed={!useNewCard}
            aria-label={`Pay with saved card ending ${selectedCard.last4}`}
            onClick={() => selectSavedCard(selectedCard.id)}
            sx={optionTargetSx}
          >
            <Box sx={radioSx(!useNewCard)}>
              <Box sx={radioDotSx(!useNewCard)} />
            </Box>
            {getBrandMark(selectedCard)}
            <Box component="span" sx={cardLineSx}>
              {formatMaskedCard(selectedCard.last4)}
            </Box>
            {selectedCard.isPrimary && (
              <Box component="span" sx={primaryPillSx}>
                PRIMARY
              </Box>
            )}
          </ButtonBase>
          {cards.length > 1 && (
            <ButtonBase
              id="checkout-change-card-btn"
              disableRipple
              aria-expanded={isPickerOpen}
              aria-controls={isPickerOpen ? "checkout-card-picker" : undefined}
              aria-label={
                isPickerOpen
                  ? "Change card, hide other saved cards"
                  : "Change card, show other saved cards"
              }
              sx={changeLinkSx}
              onClick={(event) => {
                event.stopPropagation();
                setPickerOpen((open) => !open);
              }}
            >
              Change
              <ChevronDownIcon sx={chevronSx(isPickerOpen)} />
            </ButtonBase>
          )}
        </Box>

        {isPickerOpen && (
          <Box id="checkout-card-picker" sx={pickerSx}>
            {cards.map((card) => (
              <ButtonBase
                key={card.id}
                disableRipple
                aria-pressed={card.id === selectedCardId && !useNewCard}
                aria-label={`Pay with saved card ending ${card.last4}`}
                sx={pickerRowSx(card.id === selectedCardId && !useNewCard)}
                onClick={() => selectSavedCard(card.id)}
              >
                {getBrandMark(card)}
                <Box component="span" sx={cardLineSx}>
                  {formatMaskedCard(card.last4)}
                </Box>
                {card.isPrimary && (
                  <Box component="span" sx={primaryPillSx}>
                    PRIMARY
                  </Box>
                )}
                <PickerCheckIcon
                  sx={{
                    ml: "auto",
                    color: t.color.accent,
                    opacity: card.id === selectedCardId && !useNewCard ? 1 : 0,
                  }}
                />
              </ButtonBase>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const getNewCardForm = () => (
    <>
      <Box sx={fieldGroupSx}>
        <Box
          component="span"
          id="checkout-card-information-label"
          sx={fieldLabelSx}
        >
          Card information
        </Box>
        <Box
          ref={cardFieldsRef}
          role="group"
          aria-labelledby="checkout-card-information-label"
          sx={cardBoxSx}
        >
          <Box sx={cardNumberRowSx}>
            <Box sx={stripeFieldSx}>
              <CardNumberElement
                options={{
                  ...elementOptions,
                  placeholder: "1234 1234 1234 1234",
                  // The design shows its own accepted-network chips.
                  showIcon: false,
                  disableLink: true,
                }}
                onChange={(event) =>
                  setCompleteFields((state) => ({
                    ...state,
                    number: event.complete,
                  }))
                }
              />
            </Box>
            <Box sx={networkChipRowSx}>
              {ACCEPTED_NETWORKS.map((network) => (
                <Box
                  key={network.label}
                  component="span"
                  sx={networkChipSx(network.color)}
                >
                  {network.label}
                </Box>
              ))}
            </Box>
          </Box>
          <Box sx={cardSplitRowSx}>
            <Box sx={cardSplitCellSx(true)}>
              <Box sx={stripeFieldSx}>
                <CardExpiryElement
                  options={{ ...elementOptions, placeholder: "MM / YY" }}
                  onChange={(event) =>
                    setCompleteFields((state) => ({
                      ...state,
                      expiry: event.complete,
                    }))
                  }
                />
              </Box>
            </Box>
            <Box sx={cardSplitCellSx(false)}>
              <Box sx={stripeFieldSx}>
                <CardCvcElement
                  options={{ ...elementOptions, placeholder: "CVC" }}
                  onChange={(event) =>
                    setCompleteFields((state) => ({
                      ...state,
                      cvc: event.complete,
                    }))
                  }
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={fieldGroupSx}>
        <Box component="label" htmlFor="checkout-card-name" sx={fieldLabelSx}>
          Name on card
        </Box>
        <InputBase
          id="checkout-card-name"
          placeholder="Full name"
          value={cardName}
          onChange={(event) => setCardName(event.target.value)}
          sx={textFieldSx}
        />
      </Box>

      <Box sx={fieldRowSx}>
        <Box sx={countryColumnSx}>
          <Box component="span" id="checkout-country-label" sx={fieldLabelSx}>
            Country
          </Box>
          <Select
            id="checkout-country"
            labelId="checkout-country-label"
            value={country}
            onChange={(event) => setCountry(String(event.target.value))}
            IconComponent={ChevronDownIcon}
            input={<InputBase />}
            sx={countrySelectSx}
            MenuProps={{ PaperProps: { sx: { maxHeight: 280 } } }}
          >
            {countries.map((entry) => (
              <MenuItem
                key={entry.iso2}
                value={entry.iso2}
                sx={countryMenuItemSx}
              >
                {entry.name}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Box sx={zipColumnSx}>
          <Box component="label" htmlFor="checkout-zip" sx={fieldLabelSx}>
            ZIP
          </Box>
          <InputBase
            id="checkout-zip"
            placeholder="90210"
            value={zip}
            onChange={(event) => setZip(event.target.value)}
            sx={textFieldSx}
          />
        </Box>
      </Box>
    </>
  );

  return (
    <Box sx={checkoutViewSx}>
      <ButtonBase
        id="checkout-back-btn"
        disableRipple
        sx={backLinkSx}
        onClick={props.onBack}
      >
        <CheckoutBackArrowIcon />
        Back to plans
      </ButtonBase>

      <Box sx={checkoutGridSx}>
        {getSummaryCard()}

        <Box sx={cardSx(t.space.paymentGap)}>
          <Box sx={paymentHeadSx}>
            <Box component="h2" sx={cardTitleSx}>
              Payment
            </Box>
            <Box component="span" sx={poweredBySx}>
              Powered by{" "}
              <Box component="span" sx={stripeWordmarkSx}>
                stripe
              </Box>
            </Box>
          </Box>

          {getSavedCardOption()}

          <ButtonBase
            id="checkout-new-card-option"
            disableRipple
            aria-pressed={useNewCard}
            onClick={() => {
              setUseNewCard(true);
              setPickerOpen(false);
            }}
            sx={newCardOptionSx(useNewCard)}
          >
            <Box sx={radioSx(useNewCard)}>
              <Box sx={radioDotSx(useNewCard)} />
            </Box>
            <Box component="span" sx={optionLabelSx}>
              Use a new card
            </Box>
          </ButtonBase>

          {useNewCard && getNewCardForm()}

          <Button
            id="checkout-pay-btn"
            disableElevation
            disabled={!canPay}
            aria-label={props.summary.payLabel}
            aria-busy={isPaying}
            sx={payButtonSx}
            onClick={handlePay}
          >
            {isPaying ? (
              <CircularProgress size={18} sx={{ color: t.color.onAccent }} />
            ) : (
              props.summary.payLabel
            )}
          </Button>

          <Box sx={secureNoteSx}>
            <SecureLockIcon />
            Encrypted and processed securely by Stripe
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PlanCheckout;
